import { useRef, useState } from "react";
import { Upload, X, Building2 } from "lucide-react";
import { Button } from "./ui/button";
import { toast } from "sonner";

/**
 * LogoUploader — drop / pick a company logo and get back a compressed data URL.
 *
 * We handle compression client-side (canvas resize to max 512px on the longest
 * side, JPEG q=0.9 for PNG/JPG, SVG passed through verbatim) so what we store
 * on the tenant doc is always a lean base64 blob. Any aspect ratio is allowed
 * per product decision — the parent frame is fixed and we scale-to-fit.
 *
 * Props:
 *  - value      current data URL (or empty string)
 *  - onChange   called with the new data URL (or "" when cleared)
 *  - size       display size in px (default 96) — visual container only
 *  - label      optional label above the drop-zone
 */
const MAX_DIMENSION = 512;
const MAX_BASE64_BYTES = 700_000; // ~500KB decoded — keeps tenant docs lean

export const LogoUploader = ({ value = "", onChange, size = 96, label }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [processing, setProcessing] = useState(false);

  const processFile = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (PNG, JPG, or SVG)");
      return;
    }
    setProcessing(true);
    try {
      // SVGs are already lightweight text — pass through with a size check.
      if (file.type === "image/svg+xml") {
        if (file.size > MAX_BASE64_BYTES) {
          toast.error("SVG too large — must be under 500KB");
          return;
        }
        const svgText = await file.text();
        onChange(`data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`);
        return;
      }
      const dataUrl = await compressImage(file);
      if (dataUrl.length > MAX_BASE64_BYTES) {
        toast.error("Image is too large even after compression. Try a smaller file.");
        return;
      }
      onChange(dataUrl);
    } catch (e) {
      console.error(e);
      toast.error("Could not read that image — try a different file");
    } finally {
      setProcessing(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const compressImage = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const scale = Math.min(1, MAX_DIMENSION / Math.max(img.width, img.height));
          canvas.width = Math.round(img.width * scale);
          canvas.height = Math.round(img.height * scale);
          const ctx = canvas.getContext("2d");
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          // PNGs may have transparency worth preserving; otherwise JPEG for
          // much smaller files. Rule of thumb: photos → JPEG, graphics → PNG.
          const wantsAlpha = file.type === "image/png" || file.type === "image/webp";
          resolve(canvas.toDataURL(wantsAlpha ? "image/png" : "image/jpeg", 0.9));
        };
        img.onerror = reject;
        img.src = reader.result;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) processFile(file);
  };

  return (
    <div className="space-y-2">
      {label && <p className="text-sm font-medium text-slate-700">{label}</p>}
      <div className="flex items-center gap-4">
        {/* Preview — fixed square container so parent layouts stay stable */}
        <div
          className="shrink-0 rounded-lg bg-white border border-slate-200 flex items-center justify-center overflow-hidden"
          style={{ width: size, height: size }}
          data-testid="logo-preview"
        >
          {value ? (
            <img src={value} alt="Company logo preview" className="max-w-full max-h-full object-contain" />
          ) : (
            <Building2 className="w-8 h-8 text-slate-300" />
          )}
        </div>

        {/* Drop-zone / picker */}
        <div
          className={`flex-1 rounded-lg border-2 border-dashed p-4 text-center transition-colors ${
            dragging ? "border-[#0D9488] bg-[#0D9488]/5" : "border-slate-300"
          }`}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          data-testid="logo-dropzone"
        >
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,image/svg+xml,image/webp"
            className="hidden"
            onChange={(e) => processFile(e.target.files?.[0])}
            data-testid="logo-file-input"
          />
          <p className="text-sm text-slate-600 mb-2">
            Drag & drop your logo, or
          </p>
          <div className="flex items-center justify-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => inputRef.current?.click()}
              disabled={processing}
              data-testid="logo-pick-btn"
            >
              <Upload className="w-3.5 h-3.5 mr-1.5" />
              {processing ? "Processing…" : value ? "Replace" : "Choose file"}
            </Button>
            {value && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => onChange("")}
                className="text-slate-500 hover:text-red-600"
                data-testid="logo-remove-btn"
              >
                <X className="w-3.5 h-3.5 mr-1" />
                Remove
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-400 mt-2">PNG, JPG, SVG or WebP — up to 500KB.</p>
        </div>
      </div>
    </div>
  );
};

/**
 * CompanyBadge — reusable display of the tenant logo + name for headers.
 * Falls back to a Building2 icon when no logo is set. Used in the sidebar,
 * dashboard headers, and the public Time Clock page.
 */
export const CompanyBadge = ({ logo, name, logoSize = 32, className = "", nameClassName = "" }) => {
  return (
    <div className={`flex items-center gap-2 ${className}`} data-testid="company-badge">
      <div
        className="shrink-0 rounded-md bg-white border border-slate-200 flex items-center justify-center overflow-hidden"
        style={{ width: logoSize, height: logoSize }}
      >
        {logo ? (
          <img src={logo} alt="" className="max-w-full max-h-full object-contain" data-testid="company-logo-img" />
        ) : (
          <Building2 className="w-4 h-4 text-slate-400" />
        )}
      </div>
      {name && <span className={nameClassName}>{name}</span>}
    </div>
  );
};

export default LogoUploader;
